import {
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
	useMsal,
	useIsAuthenticated,
	useAccount,
} from '@azure/msal-react';
import AppNav from '../components/app-nav';
import Site from '../components/site';
import {
	InteractionRequiredAuthError,
	InteractionStatus,
} from '@azure/msal-browser';
import { loginRequest } from '../utils/auth/authConfig';
import { useEffect, useState, useRef } from 'react';
import ApiService from '../utils/services/ApiService';
import { User } from '../utils/models/user';
import { SiteModel } from '../utils/models/site';
import { Virtuoso } from 'react-virtuoso';
import store from 'utils/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
	saveTestResults,
	getTestResults,
	cleanupTestResults,
	getAllFailedRequests,
	removeFailedRequest,
	getSyncCache,
	emptyAllCatchSync,
} from '../utils/services/idbService';
import Paper from '@mui/material/Paper';
import userflow from 'userflow.js';
import { IReduxState } from 'utils/models/redux-states';
import { IGroups } from 'utils/models/groups';
import { setUserInformation, setLoginSession } from 'utils/redux/reducers/userReducer';
import { setCompletedSitesLoading } from 'utils/redux/reducers/completedSitesLoadingReducer';
import { checkIfUserIsOwner } from 'utils/functions/checkSitePermission';
import {
	setAllSitesLoaded,
	setAllSitesData,
	updateSingleSite,
} from 'utils/redux/reducers/allSitesLoadedReducer';
import {
	gatherDatabaseIDs,
	updateCachedDataPointIDs,
} from 'utils/functions/updateDataPoint';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { useNavigate } from 'react-router-dom';
import { usePopupMessage } from '../hooks/SendPopUpMessage';

const Home = () => {
	const dispatch = useDispatch(); // Get the dispatch function from the useDispatch hook
	const { instance, inProgress } = useMsal();
	const reduxState = useSelector((state: IReduxState) => state.groupSites);
	const reduxUpdateDataState = useSelector(
		(state: any) => state.updateReduxData
	);
	const isCompletedSiteLoading = useSelector(
		(state: any) => state.completedSitesLoading
	);
	const isDataUpdated = reduxUpdateDataState.updateReduxData;
	const isAuthenticated = useIsAuthenticated();
	const { accounts } = useMsal();
	const account = useAccount(accounts[0] || {});
	const [siteList, setSiteList] = useState<SiteModel[]>([]);
	const [siteOriginalList, setSiteOriginalList] = useState<SiteModel[]>([]);
	const [siteDialogs, setSiteDialogs] = useState<Record<number, { open: boolean; tab: number }>>({});
	const [user, setUser] = useState<User | null>(null);
	const [userPerm, setUserPerm] = useState<number>(3);
	const accessToken = store.getState().tokens.accessToken;
	const allSitesList = useSelector(
		(state: any) => state.allSitesLoaded.allSitesData
	);
	const [isOffline, setIsOffline] = useState(!navigator.onLine);
	const [allSitesLists, setAllSitesLists] = useState<any[]>([]);
	const [testKitsUnits, setTestKitsUnits] = useState<{ [key: string]: any }>(
		[]
	);
	const [allDataLoaded, setAllDataLoaded] = useState(false);
	const [updatedLocations, setUpdatedLocations] = useState<any[]>([]);
	const [syncedTimeStamps, setSyncedTimeStamps] = useState<string>();
	const [emptiedSync, setEmptiedSync] = useState<boolean>(false);
	const [isInvalidUser, setIsInvalidUser] = useState(false);

	const reduxLocations = useSelector((state: any) => state.updateLocation);
	const userInformationString = localStorage.getItem('user_information');

	const userInformation = userInformationString
		? JSON.parse(userInformationString)
		: null;
	const userId = userInformation ? userInformation.id : null;

	const virtuosoRef = useRef<any>(null);
	const previousAccountsRef = useRef<any>(null);
	const navigate = useNavigate();
	const sendPopUpMessage = usePopupMessage();

	useEffect(() => {
		// This function updates the state based on online/offline status.
		const updateOnlineStatus = () => {
			setIsOffline(!navigator.onLine);
		};

		dispatch(setCompletedSitesLoading(false));

		// Add event listeners
		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		// Cleanup: remove event listeners on unmount to avoid memory leaks.
		return () => {
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
		};
	}, []);

	const handleGroupedSites = (groups: IGroups, listToCompare: any) => {
		if (!groups.sites) return setSiteList([]);

		const sortedGroupSites = [...groups.sites].sort(
			(a, b) => a.sortOrder - b.sortOrder
		);

		const result = [];
		for (const groupSite of sortedGroupSites) {
			for (const site of siteOriginalList) {
				const listToCompareId = site.id;
				const groupSiteId = groupSite.id;
				if (listToCompareId === groupSiteId) {
					result.push(site);
				}
			}
		}
		setSiteList(result);
	};

	const renderSitesFromSelectedGroup = (selectedGroup: IGroups) => {
		const isSiteListEmpty = siteList ? siteList.length === 0 : true;
		const listToCompare = isSiteListEmpty ? siteOriginalList : siteList;
		if (selectedGroup.name === 'ALL SITES' || !selectedGroup.sites) {
			return setSiteList(siteOriginalList);
		}
		return handleGroupedSites(selectedGroup, listToCompare);
	};

	const initializeUserFlow = (userData: any) => {
		if (accounts[0]?.localAccountId) {
			const token: string = process.env.REACT_APP_USERFLOW_TOKEN!;
			const currentDate = new Date().toISOString();
			userflow.init(token);
			userflow.identify(accounts[0].localAccountId, {
				name: userData.firstName,
				email: accounts[0].username,
				signed_up_at: currentDate,
				user_type: userData.userType,
				user_role: userData.userRole,
				first_name: userData.firstName,
				last_name: userData.lastName,
				org_name: userData.organizationVO?.name,
				is_mobile: false,
			});
		}
	};

	const identifyFullStoryUser = (userData: User) => {
		const fullStory = (window as Window & {
			FS?: {
				identify?: (userId: string, userProperties: { email: string; username: string }) => void;
			};
		}).FS;
		const email = accounts[0]?.username;
		const userId = userData.id ?? accounts[0]?.localAccountId ?? email;

		if (email && userId && fullStory?.identify) {
			fullStory.identify(String(userId), {
				email,
				username: userData.username || email,
			});
		}
	};

	const logSignIn = async () => {
		if (previousAccountsRef.current === accounts) return;
		previousAccountsRef.current = accounts;
		if (accounts)
			addEventLog("Sign In Success", "Session Management");
	}
	useEffect(() => {
		// This is the initial data when the user first land on this screen
		let localStorageFlag = false;
		const fetchUserData = async () => {
			try {
				if (accounts[0]?.username && accessToken) {
					localStorage.setItem('auth_token', accessToken);
					if (navigator.onLine) {
						const userResponse = await ApiService.getUser(
							accounts[0].username
						);

						if (userResponse.data.status !== 'Active' && userResponse.data.status !== 'Activation Pending') {
							
							addEventLog("Sign in Failed", "Session Management", undefined, undefined, "User status is " + userResponse.data.status);

							setIsInvalidUser(true);
							sendPopUpMessage(
								'Error',
								'Unable to process your request, please contact support.'
							);

							await new Promise(resolve => setTimeout(resolve, 5000));

							localStorage.removeItem('loginRedirectedFrom');
							localStorage.removeItem('auth_token');
							await instance.logoutRedirect({
								account: accounts[0],
								postLogoutRedirectUri: '/',
							});

							return;

						}

						userResponse.data.email = accounts[0].username;
						setUser(userResponse.data);
						if (userResponse.data.userPermissionVOs) {
							setUserPerm(
								userResponse.data.userPermissionVOs[0].level
							);
						}
						localStorage.setItem('currentUser', accounts[0].username);
						localStorage.setItem(
							'userName',
							`${userResponse.data.firstName} ${userResponse.data.lastName}`
						);
						identifyFullStoryUser(userResponse.data);
						localStorageFlag =
							userResponse?.data?.localStorage ?? false;
						localStorage.setItem(
							'localStorage',
							JSON.stringify(localStorageFlag)
						);
						initializeUserFlow(userResponse?.data);
						dispatch(setUserInformation(userResponse?.data));
						localStorage.setItem(
							'user_information',
							JSON.stringify(userResponse?.data)
						);
						dispatch(setLoginSession(Date.now().toString()));
						logSignIn();
						if (userResponse?.data?.id) {
							localStorage.setItem(
								'userDetails',
								JSON.stringify(userResponse.data)
							);
						}
					} else {
						const swRegistration = await navigator.serviceWorker
							.ready;
						swRegistration.sync.register('mySync');
						console.log('Sync done');
					}
				}
			} catch (error) {
				console.error('Error fetching user data:', error);
			}
		};

		fetchUserData();
	}, [accounts]);

	useEffect(() => {
		if (!user?.id) return;
		const fetchAllData = async () => {
			try {
				if (account?.username && accessToken) {

					//Check for sync cache first and wait for an update
					if (!emptiedSync) {
						await syncData();
					}
					
					const sites = await ApiService.getAllDataNew(user?.id);
					const allSites = sites?.data?.groupAllVOs?.find(
						(site: { name: string }) => site.name === 'ALL SITES'
					);
					const username = account?.username ?? 'unknown';

					if (localStorage.getItem('localStorage') === 'true') {
						//Need to only use existing cache's data points
						try {
							let cachedData = await getTestResults(
								`allSites`,
								username
							);
							let cachedLocations: any[] = [];
							cachedData.forEach((site: any) => {
								site.sampleLocationAllVOs.forEach(
									(location: any) => {
										cachedLocations.push(location);
									}
								);
							});

							allSites?.siteAllVOs.forEach((site: any) => {
								site.sampleLocationAllVOs.forEach(
									(location: any) => {
										let cacheLoc = cachedLocations.find(
											(cachedLocation: any) =>
												location.id ===
												cachedLocation.id
										);
										location.dataPointAllVOs =
											cacheLoc.dataPointAllVOs;
									}
								);
							});
						} catch (error) {
							console.log("Error when merging data points. Proceeding.", error);
						}
					}

					const defaultKits = sites?.data?.defaultTestKits;
					dispatch(setAllSitesData(allSites?.siteAllVOs));
					setAllSitesLists(allSites?.siteAllVOs);
					setSiteOriginalList(allSites?.siteAllVOs);
					const allDescriptorsMap: { [key: string]: any } = {};
					const seenOutputIds = new Set<string>();

					defaultKits.forEach((kit: any) => {
						const descriptorsMap: { [key: string]: any } = {}; // Temporary map to store descriptors for the current kit

						kit.fieldDescriptorAllVOs.forEach((descriptor: any) => {
							// If the outputId has not been seen before, add it to the map
							if (!seenOutputIds.has(descriptor.outputId)) {
								descriptorsMap[descriptor.outputId] =
									descriptor;
								seenOutputIds.add(descriptor.outputId);
							} else if (descriptor.unit) {
								// If it's a duplicate but the current unit is not empty, replace the old entry
								descriptorsMap[descriptor.outputId] =
									descriptor;
							}
						});

						// Save the descriptorsMap under the current testKitName in allDescriptorsMap
						allDescriptorsMap[kit.id] = descriptorsMap;
					});
					setTestKitsUnits(allDescriptorsMap);
					await saveTestResults(
						`allSites`,
						allSites?.siteAllVOs,
						username
					);
					await saveTestResults(
						`defaultTestKits`,
						defaultKits,
						username
					);
					await saveTestResults(
						`allDataPointsUnits`,
						[allDescriptorsMap] as any,
						username
					);
					dispatch(setAllSitesLoaded(true));
					setAllDataLoaded(true);
					if (localStorage.getItem('loginRedirectedFrom') === "reports") {
						localStorage.setItem('loginRedirectedFrom', "");
						navigate('/reports');
					}
				}
			} catch (error) {
				console.error('Error getting user data:', error);
			}
		};
		fetchAllData();
	}, [user,syncedTimeStamps]);

	useEffect(() => {
		// This is when someone changes the group from the top nav dropdown
		getGroups();
		if (!reduxState.sitesInformation) {
			setSiteList(siteOriginalList);
		}
		renderSitesFromSelectedGroup(reduxState.sitesInformation);
	}, [reduxState, syncedTimeStamps, isDataUpdated]);

	useEffect(() => {
		// This is when someone changes the group from the top nav dropdown
		getGroupsOriginal();
		if (!reduxState.sitesInformation) {
			setSiteList(siteOriginalList);
		}
		renderSitesFromSelectedGroup(reduxState.sitesInformation);
	}, [siteOriginalList, syncedTimeStamps]);

	const getGroups = async () => {
		try {
			if (user) {
				let cachedData = await getTestResults(`allSites`, user.email);
				setSiteOriginalList(cachedData);
			}
		} catch (error) {}
	};

	const getGroupsOriginal = async () => {
		try {
			if (user && !siteOriginalList) {
				let cachedData = await getTestResults(`allSites`, user.email);
				dispatch(setAllSitesData(cachedData));
				setSiteOriginalList(cachedData);
			}
		} catch (error) {}
	};

	useEffect(() => {
		// Set a timeout for 3 seconds
		const timeout = setTimeout(() => {
		}, 3000);

		return () => clearTimeout(timeout); // Clean up the timeout on unmount
	}, [siteList]);

	useEffect(() => {
		const initializeAuth = async () => {
			if (navigator.onLine) {
				if (!isAuthenticated && inProgress === InteractionStatus.None) {
					try {
						await instance.loginRedirect(loginRequest);
						localStorage.removeItem('authInitiated');
					} catch (error) {
						console.error('Error during loginRedirect:', error);
					}
				}

				if (isAuthenticated && account) {
					try {
						const response = await instance.acquireTokenSilent({
							...loginRequest,
							account,
						});
						ApiService.setTokenToCookie(response.accessToken);
					} catch (error) {
						console.error('Error acquiring token silently:', error);
						if (error instanceof InteractionRequiredAuthError) {
							await instance.acquireTokenRedirect(loginRequest);
						}
					}
				}
			}
		};

		initializeAuth();
	}, [isAuthenticated, inProgress, instance, account]);

	const isSameLocationAndTestKit = (data1: any, data2: any) => {
		return (
			data1.samplePointId === data2.samplePointId &&
			data1.testkitName === data2.testkitName
		);
	};

	const mapCachedData = async (
		cachedData: any,
		visibleColumns: any[],
		isLatest: boolean
	) => {
		const valueVOs = cachedData
			.filter((item: any) => !!item.new_entry) // Step 1: Filter items with new_entry
			.map((item: any) => {
				const visibleColumn = visibleColumns.find(
					(col) => col.colName === item.displayName
				);
				return {
					displayName: item.displayName,
					outputAlarm: item.outputAlarm,
					outputId: item.outputId,
					outputValue: item.outputValue,
					visible: visibleColumn ?? false,
				};
			})
			.filter(Boolean);

		const username = localStorage.getItem('userName') ?? 'unknown';
		const matchedData = cachedData.filter((data: any) => !!data.new_entry);
		const user = localStorage.getItem('currentUser') ?? 'unknown_user';
		const siteId = matchedData[0]?.siteId || 'unknown_site'; // Assuming siteId is available in the scope

		const normalizeCatchDataPoints = cachedData.map((dp: any) => {
			const { new_entry, ...rest } = dp;
			return rest;
		});
		await saveTestResults(
			`dataPoints_all_${siteId}`,
			normalizeCatchDataPoints,
			user
		);

		return {
			id: matchedData[0]?.new_entry,
			latestData: isLatest,
			note: matchedData[0]?.note ?? '',
			operator: username,
			testKitId: matchedData[0]?.testKitId ?? 2,
			testKitName: matchedData[0]?.testkitName,
			timestamp: matchedData[0]?.timestamp ?? Date.now(),
			deviceSerial: matchedData[0]?.deviceSerial ?? '',
			operatorEmail: matchedData[0]?.operatorEmail ?? '',
			valueVOs,
		};
	};

	const processDataInSampleLocations = async (
		sampleLocations: any[],
		cachedData: any,
		visibleColumns: any[],
		targetSamplePointId: any
	) => {
		const updatedLocations = await Promise.all(
			sampleLocations.map(async (location) => {
				if (location.id !== targetSamplePointId) {
					return location;
				}

				const { dataPointAllVOs } = location;

				for (let i = 0; i < dataPointAllVOs.length - 1; i++) {
					const currentData = dataPointAllVOs[i];
					const nextData = { ...(dataPointAllVOs[i + 1] ?? {}) };

					if (isSameLocationAndTestKit(currentData, nextData)) {
						nextData.disabled = true;
					}
				}

				//check for latestData
				let latestDataPoint = dataPointAllVOs.find(
					(dp: any) => dp.latestData === true
				);
				let isLatest = true;
				if (
					latestDataPoint?.timestamp <
					cachedData[cachedData.length - 1].timestamp
				) {
					//do nothing
				} else if (latestDataPoint) {
					isLatest = false;
				}

				let mappedData: any = {};
				try {
					mappedData = await mapCachedData(
						cachedData,
						visibleColumns,
						isLatest
					);
				} catch (error) {
					console.error(error);
				}

				const updatedData = [...dataPointAllVOs, mappedData];
				return { ...location, dataPointAllVOs: updatedData };
			})
		);

		return updatedLocations;
	};

	// Function to process the data
	const processData = async (
		allData: any[],
		siteId: any,
		cachedData: any,
		samplePointId?: number
	) => {
		const siteData = allSitesList.find((site: any) => site.id === siteId);
		if (siteData) {
			const username = account?.username ?? 'unknown';
			let allSitesFromCache = await getTestResults(`allSites`, username);

			const { sampleLocationAllVOs, tableColumnSettingsAllVOs } =
				siteData;

			const visibleColumns = tableColumnSettingsAllVOs.filter(
				(col: any) => col.visible
			);

			processDataInSampleLocations(
				sampleLocationAllVOs,
				cachedData,
				visibleColumns,
				samplePointId
			)
				.then((res) => {
					dispatch(
						updateSingleSite({
							updatedSampleLocations: res,
							siteId,
						})
					);

					//add the new data point to the cache, if we're in local cache mode
					if (localStorage.getItem('localStorage') === 'true') {
						let updatedLocation = res.find(
							(sampleLoc: any) =>
								sampleLoc.id ===
								cachedData[cachedData.length - 1].samplePointId
						);
						let updatedDataPoint =
							updatedLocation.dataPointAllVOs.find(
								(dataPoint: any) =>
									dataPoint.id ===
									cachedData[cachedData.length - 1].new_entry
							);
						let incomingDataPoint =
							structuredClone(updatedDataPoint);

						incomingDataPoint.valueVOs.forEach((value: any) => {
							value.visible = value.visible?.visible ?? false;
						});

						let cachedSiteToUpdate = allSitesFromCache.find(
							(site: any) => site.id === siteId
						);
						let cachedLocationToUpdate =
							cachedSiteToUpdate.sampleLocationAllVOs.find(
								(sampleLoc: any) =>
									sampleLoc.id ===
									cachedData[cachedData.length - 1]
										.samplePointId
							);

						//update latestData flag on cache, based on timestamp
						let latestDataPoint =
							cachedLocationToUpdate.dataPointAllVOs.find(
								(dp: any) => dp.latestData === true
							);
						if (
							latestDataPoint?.timestamp <
							incomingDataPoint.timestamp
						) {
							latestDataPoint.latestData = false;
						} else if (latestDataPoint) {
							incomingDataPoint.latestData = false;
						}

						cachedLocationToUpdate.dataPointAllVOs.unshift(
							incomingDataPoint
						);

						saveTestResults(
							`allSites`,
							allSitesFromCache,
							username
						);
					}
				})
				.catch((error) => {
					console.error(error);
				});

			if (localStorage.getItem('localStorage') !== 'true') {
				await saveTestResults(`allSites`, allSitesList, username);
			}
		}
	};
	const refreshCachedData = async (siteId: number, samplePointId?: number) => {
		try {
			const username = account?.username ?? 'unknown';
			const cachedData = await getTestResults(
				`dataPoints_all_${siteId}`,
				username
			);
			if (cachedData && Array.isArray(cachedData)) {
				processData(allSitesList, siteId, cachedData, samplePointId);
			}
		} catch (error) {
			console.error(
				`Error fetching cached data for site ${siteId}:`,
				error
			);
		}
		try {
			if (localStorage.getItem('localStorage') !== 'true') {
				const sites = await ApiService.getAllDataNew(
					user?.id as number
				);
				const allSites = sites?.data?.groupAllVOs?.find(
					(site: { name: string }) => site.name === 'ALL SITES'
				);
				const username = account?.username ?? 'unknown';

				const defaultKits = sites?.data?.defaultTestKits;
				dispatch(setAllSitesData(allSites?.siteAllVOs));
				await cleanupTestResults();
				await saveTestResults(
					`allSites`,
					allSites?.siteAllVOs,
					username
				);
				await saveTestResults(`defaultTestKits`, defaultKits, username);
			}
		} catch (error) {
			console.error(
				`Error fetching online data for site ${siteId}:`,
				error
			);
		}
	};
	const saveAllSiteDB = async () => {
		const username = account?.username ?? 'unknown';
		if (localStorage.getItem('localStorage') !== 'true') {
			await saveTestResults(`allSites`, [...(allSitesList ?? [])], username);
		}
	};
	useEffect(() => {
		setUpdatedLocations(() => reduxLocations?.updateLocation);
	}, [reduxLocations]);
	useEffect(() => {
		setAllSitesLists(() => [...(allSitesList ?? [])]);
		saveAllSiteDB();
	}, [allSitesList]);

	const [loadingMessage, setLoadingMessage] = useState(
		<span>
			Loading your account
			<img
				src="/assets/placeholders/ellipsis.svg"
				style={{ display: 'inline-block', margin: '8px 0px 0 -2px' }}
			/>
		</span>
	);
	useEffect(() => {
		const timer1 = setTimeout(() => {
			setLoadingMessage(
				<span>
					Your data is on the way!
					<br />
					Just another moment
					<img
						src="/assets/placeholders/ellipsis.svg"
						style={{
							display: 'inline-block',
							margin: '8px 0px 0 -2px',
						}}
					/>
				</span>
			);
		}, 6000);
		const timer2 = setTimeout(() => {
			setLoadingMessage(
				<span>
					<strong>Still loading. Thanks for your patience.</strong>{' '}
					<br />
					
					<span
						style={{
							fontSize: '13px',
							display: 'inline-block',
							marginTop: '15px',
							maxWidth: '400px',
							fontStyle: 'italic',
							borderTop: '1px solid white',
							paddingTop: '15px',
						}}
					>
						<strong>Tip:</strong> If your load time is consistently this slow, <br></br>contact our support team for assistance.
					</span>
				</span>
			);
		}, 15000);
		return () => {
			clearTimeout(timer1);
			clearTimeout(timer2);
		};
	}, []);
	const getAllFaildsReqs = async () => {
		const allReq = await getAllFailedRequests();
		const tempIdToRealIdMap = new Map();

		for (let i = 0; i < allReq.length; i++) {
			try {
				let ele = allReq[i];
				ApiService.callFailsApis(
					ele.url,
					JSON.parse(ele.body),
					ele.method.toLowerCase()
				)
					.then((response: any) => {
						removeFailedRequest(ele.key);
						if (ele.tempID) {
							let dataAndLocationIds = {
								dataPointId: ele.tempID,
								locationId: response.data.samplePointId,
							};
							tempIdToRealIdMap.set(
								dataAndLocationIds,
								gatherDatabaseIDs(response.data)
							);
						}
						if (i === allReq.length - 1) {
							updateCachedDataPointIDs(tempIdToRealIdMap)
						}
					})
					.catch((e) => console.log(e));
			} catch (error) {
				console.log(error);
			}
		}
	};
	const sanitizeSyncData = (data: any) => {
		if (data.siteSyncs) {
			data.siteSyncs = data.siteSyncs.map((site: any) => {
				const sanitizedSite = { ...site };
				delete sanitizedSite.sampleLocationAllVOs;
				delete sanitizedSite.sharedUserVOs;
				delete sanitizedSite.timestamp;

				if (Array.isArray(sanitizedSite.tableColumnSettingsAllVOs)) {
					sanitizedSite.tableColumnSettingsAllVOs = sanitizedSite.tableColumnSettingsAllVOs.map((col: any) => {
						const sanitizedCol = { ...col };
						delete sanitizedCol.desktop;
						delete sanitizedCol.mobile;
						delete sanitizedCol.siteId;
						delete sanitizedCol.userId;
						return sanitizedCol;
					});
				}

				return sanitizedSite;
			});
		}
		return data;
	};
	const syncData = async () => {
		try {
			if (user) {
				const data = await getSyncCache();
				const sanitizedData = sanitizeSyncData(data);
				await ApiService.callFailsApis(
					process.env.REACT_APP_BACKEND_URI + "/cwds/services/dataservice/uploadsyncdata",
					JSON.parse(JSON.stringify(sanitizedData)),
					"post"
				).then(async()=>{
					await emptyAllCatchSync();
					setEmptiedSync(true);
					setSyncedTimeStamps(new Date().toISOString());
				}).catch((error) => {
					console.log(error);
				});
			}
		} catch (error) {
			console.log(error);
		}
	}
	useEffect(() => {
		if (!isOffline) {
			syncData();
			getAllFaildsReqs();
		}
	}, [isOffline]);

	function updateViewedUser(newUserId: number) {
		if (user) {
			let newUser = structuredClone(user);
			newUser.id = newUserId;
			setUser(newUser);
		}
	}

	const handleRunTestsClicked = (index: number) => {
		if (virtuosoRef.current) {
			requestAnimationFrame(() => {
					virtuosoRef.current.scrollToIndex({
						index: index,
						behavior: 'smooth',
					});
			});
		}
	};

	function footer() {
		return (
			<div style={{ paddingTop: '200px' }} />
		)
	}

	return (
		<>
			<AuthenticatedTemplate>
				{isInvalidUser ? null : (
					<>
				<AppNav
					user={user}
					groupsReady={allDataLoaded || isOffline}
					onViewedUserUpdate={(newUserId: number) =>
						updateViewedUser(newUserId)
					}
					syncedTimeStamps={syncedTimeStamps}
				/>
				<div className="no-print">
				{/* Offline mode notification */}
				{isOffline && (
					<Paper
						elevation={1}
						style={{
							backgroundColor: '#F0F0F0',
							padding: '10px',
							textAlign: 'center',
							position: 'relative',
							zIndex: '100',
						}}
					>
						<span
							style={{
								fontFamily: 'Inter',
								fontWeight: '500',
								color: '#556670',
								fontSize: '14px',
							}}
						>
							Offline Mode:
						</span>
						<span
							style={{
								fontFamily: 'Jost',
								fontWeight: 'normal',
								color: '#556670',
								fontSize: '14px',
							}}
						>
							{' '}
							Your test results will save locally. Other features
							are limited.
						</span>
					</Paper>
				)}
				{/* Placeholder for empty site list */}
				{!isCompletedSiteLoading.completedSitesLoading ||
				!allDataLoaded ? (
					<div
						style={{
							position: 'fixed',
							height: '100vh',
							width: '100vw',
							background: 'rgba(255,255,255,.0)',
							top: '0px',
							zIndex: '1000000',
							overflow: 'hidden',
							backdropFilter: 'blur(1.4px)',
						}}
					>
						<div
							className="wave-container"
							style={{
								margin: '60px auto 0',
								position: 'fixed',
								height: '100vh',
								background: '#0092c8',
								top: '0px',
								zIndex: '1000001',
							}}
						>
							<img
								src="/assets/placeholders/Wave@1x-10.0s-1920px-720px.svg"
								alt="Loading..."
								style={{ marginTop: '0px' }}
							/>
						</div>
						<div
							id="loading"
							className="flex justify-center"
							style={{ position: 'relative', zIndex: '1000002' }}
						>
							<div
								style={{
									margin: '33vh auto 0',
									maxWidth: '100%',
									color: 'white',
									fontFamily: 'jost',
									fontSize: '18px',
									textAlign: 'center',
								}}
							>
								<img
									src="/assets/logos/LuminUltra_Main_Logo_White_With_Tag2.svg"
									alt="LuminUltra logo"
									style={{
										margin: '0 0 30px 0',
										width: '440px',
									}}
								/>
								{loadingMessage}
							</div>
						</div>
					</div>
				) : allSitesLists.length === 0 ? (
					<div>
						<div
							className="flex justify-center site"
							style={{ margin: '70px auto 0' }}
						>
							<img
								src="/assets/placeholders/site-placeholder.svg"
								alt="Loading"
							/>
						</div>
						<div
							id="no-sites"
							className="flex justify-center !text-primary-grey"
							style={{ marginTop: '-120px', fontFamily: 'jost' }}
						>
							To start testing, create a{' '}
							<span style={{ fontWeight: '500' }}>
								&nbsp;Site&nbsp;
							</span>{' '}
							from the main menu.
						</div>
					</div>
				) : (
					// Render with dynamic loading
					<Virtuoso
						computeItemKey={(index) => {
							const orderedSite = siteList.length > allSitesLists.length
								? allSitesLists[index]
								: siteList[index];
							return orderedSite?.id ?? index;
						}}
						ref={virtuosoRef}
						//typically tests moves the screen down, so extra space as been alloted to the bottom to prevent loss of data
						increaseViewportBy={{ top: window.innerHeight*0.5, bottom: window.innerHeight*0.5}}
						style={{
							width: '100%',
							overflow: 'auto',
							height: 'calc(100vh - 52px)',
						}}
						components={{ Footer: footer }}
						context={{ virtuosoRef }}
						totalCount={
							siteList.length > allSitesLists.length
								? allSitesLists.length
								: siteList.length
						}
						itemContent={(index) => {
							const orderedSite =
								siteList.length > allSitesLists.length
									? allSitesLists[index]
									: siteList[index];
							let site = allSitesLists.find(
								(site) => site.id === orderedSite.id
							);
							let dataPoints = [] as any;
							dataPoints = structuredClone(
								site?.sampleLocationAllVOs
							);
							if (updatedLocations.length) {
								const index =
									site?.sampleLocationAllVOs?.findIndex(
										(vos: any, index: number) =>
											vos?.name !==
												updatedLocations[index]?.name &&
											vos?.id ===
												updatedLocations[index]?.id
									);
								if (index > -1) {
									dataPoints[index].name =
										updatedLocations[index].name;
								}
							}
							return (
								<div
									className="site"
									style={{ marginTop: '70px' }}
								>
									<Site
										virtuosoRef={virtuosoRef}
										siteId={site?.id ?? orderedSite.id}
										index={index}
										testKitsUnits={testKitsUnits}
										siteName={site?.name ?? ''}
										samplePoint={site?.sampleLocationAllVOs[0]}
										tableColumnSettingsAllVOs={site?.tableColumnSettingsAllVOs}
										dataPoints={dataPoints}
										userId={userId}
										isOwner={checkIfUserIsOwner(site, userId, userPerm)}
										onTestDone={(siteId, samplePointId) => refreshCachedData(site.id, samplePointId)}
										onRunTestsClicked={(index) => handleRunTestsClicked(index)}
										dialogOpen={siteDialogs[site?.id]?.open ?? false}
										setDialogOpen={(open: boolean) => setSiteDialogs(prev => ({
											...prev,
											[site.id]: { tab: prev[site.id]?.tab ?? 0, open },
										}))}
										currentTab={siteDialogs[site?.id]?.tab ?? 0}
										setCurrentTab={(tab: React.SetStateAction<number>) => setSiteDialogs(prev => {
											const cur = prev[site.id]?.tab ?? 0;
											return { ...prev, [site.id]: { open: prev[site.id]?.open ?? false, tab: typeof tab === 'function' ? tab(cur) : tab } };
										})}
									>
										<p>
											{dataPoints?.length
												? `Latest data points: ${JSON.stringify(
														dataPoints
												  )}`
												: 'No data available'}
										</p>
									</Site>
								</div>
							);
						}}
					/>
				)}
				</div>
				</>
				)}
			</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				{/* Content for unauthenticated users */}
			</UnauthenticatedTemplate>
		</>
	);
};
export default Home;